import { 
  getFirestore, 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy,
  serverTimestamp,
  increment,
  setDoc
} from 'firebase/firestore';
import { auth } from './auth';
import { User, CreditApprover, ApproverRequest, UserProfile } from '../types/user';
import { COLLECTIONS } from '../constants';
import { getErrorMessage } from '../utils/errorHandling';

const db = getFirestore();

// Helper to get current user ID
const getCurrentUserId = () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Please sign in to continue. You need to be authenticated to access your profile.');
  }
  return user.uid;
};

export const userService = {
  // Get or create user profile
  async getUserProfile(): Promise<UserProfile> {
    try {
      const userId = getCurrentUserId();
      const user = auth.currentUser!;
      
      console.log('Getting user profile for:', { userId, userEmail: user.email });
      
      const q = query(
        collection(db, COLLECTIONS.USER_PROFILES),
        where('uid', '==', userId)
      );

      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        console.log('Creating new user profile');
        // Create new user profile
        const newProfile: Omit<UserProfile, 'id'> = {
          uid: userId,
          email: user.email!,
          displayName: user.displayName || user.email!,
          photoURL: user.photoURL || undefined,
          hasApprovers: false,
          canCreateCreditedTasks: false,
          totalApprovers: 0,
          activeApprovers: 0,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        // Use setDoc with UID as document ID
        await setDoc(doc(db, COLLECTIONS.USER_PROFILES, userId), {
          ...newProfile,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        }, { merge: true });
        console.log('User profile created:', { id: userId, ...newProfile });
        return { id: userId, ...newProfile };
      }

      const docSnap = querySnapshot.docs[0];
      const data = docSnap.data();
      
      console.log('User profile found:', { id: docSnap.id, ...data });
      return {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      } as UserProfile;
    } catch (error) {
      console.error('Error getting user profile:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Update user profile
  async updateUserProfile(updates: Partial<UserProfile>): Promise<void> {
    try {
      const userId = getCurrentUserId();
      const q = query(
        collection(db, COLLECTIONS.USER_PROFILES),
        where('uid', '==', userId)
      );

      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const docRef = querySnapshot.docs[0].ref;
        await updateDoc(docRef, {
          ...updates,
          updatedAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Request credit approver
  async requestCreditApprover(approverEmail: string): Promise<void> {
    try {
      const userId = getCurrentUserId();
      const user = auth.currentUser!;
      
      console.log('Requesting credit approver:', { userId, userEmail: user.email, approverEmail });
      
      // Check if approver already exists
      const existingApprover = await this.getCreditApprover(approverEmail);
      if (existingApprover) {
        throw new Error('This person is already your credit approver or has a pending request.');
      }

      // Create approver request in credit_approvers collection
      const approverRequest = {
        userId,
        requesterEmail: user.email!.toLowerCase(),
        requesterName: user.displayName || user.email!,
        approverEmail: approverEmail.toLowerCase(),
        status: 'pending',
        requestedAt: serverTimestamp()
      };

      console.log('Creating credit approver document:', approverRequest);
      await addDoc(collection(db, COLLECTIONS.CREDIT_APPROVERS), approverRequest);

      console.log('Credit approver request created successfully');

    } catch (error) {
      console.error('Error requesting credit approver:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Get credit approvers for current user
  async getCreditApprovers(): Promise<CreditApprover[]> {
    try {
      const userId = getCurrentUserId();
      
      const q = query(
        collection(db, COLLECTIONS.CREDIT_APPROVERS),
        where('userId', '==', userId),
        where('status', '==', 'accepted'),
        orderBy('requestedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          requestedAt: data.requestedAt?.toDate() || new Date(),
          respondedAt: data.respondedAt?.toDate() || undefined
        };
      }) as CreditApprover[];
    } catch (error) {
      console.error('Error getting credit approvers:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Get specific credit approver
  async getCreditApprover(approverEmail: string): Promise<CreditApprover | null> {
    try {
      const userId = getCurrentUserId();
      
      const q = query(
        collection(db, COLLECTIONS.CREDIT_APPROVERS),
        where('userId', '==', userId),
        where('approverEmail', '==', approverEmail.toLowerCase())
      );

      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }

      const doc = querySnapshot.docs[0];
      const data = doc.data();
      
      return {
        id: doc.id,
        ...data,
        requestedAt: data.requestedAt?.toDate() || new Date(),
        respondedAt: data.respondedAt?.toDate() || undefined
      } as CreditApprover;
    } catch (error) {
      console.error('Error getting credit approver:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Remove credit approver
  async removeCreditApprover(approverId: string): Promise<void> {
    try {
      const userId = getCurrentUserId();
      
      // Verify the approver belongs to the current user
      const approver = await this.getCreditApprovers();
      const targetApprover = approver.find(a => a.id === approverId);
      
      if (!targetApprover) {
        throw new Error('Approver not found.');
      }

      await deleteDoc(doc(db, COLLECTIONS.CREDIT_APPROVERS, approverId));
      
      // Update user profile
      const updatedApprovers = approver.filter(a => a.id !== approverId);
      const activeApprovers = updatedApprovers.filter(a => a.status === 'accepted').length;
      
      await this.updateUserProfile({
        totalApprovers: updatedApprovers.length,
        activeApprovers,
        hasApprovers: updatedApprovers.length > 0,
        canCreateCreditedTasks: activeApprovers > 0
      });

    } catch (error) {
      console.error('Error removing credit approver:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Remove a credit approver by document ID
  async removeCreditApproverByApproverUid(approverId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, COLLECTIONS.CREDIT_APPROVERS, approverId));
    } catch (error) {
      console.error('Error removing credit approver:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Get pending approver requests for current user (where they are the approver)
  async getPendingApproverRequests(): Promise<ApproverRequest[]> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Please sign in to continue.');
      }

      // Query for requests where the current user's email matches the approverEmail
      // This shows requests where someone is asking the current user to be their approver
      const q = query(
        collection(db, COLLECTIONS.CREDIT_APPROVERS),
        where('approverEmail', '==', user.email?.toLowerCase()),
        where('status', '==', 'pending'),
        orderBy('requestedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      
      // Process each request and fetch user profile if needed
      const requests = await Promise.all(querySnapshot.docs.map(async doc => {
        const data = doc.data();
        let requesterName = data.requesterName;
        let requesterEmail = data.requesterEmail;
        
        // If we don't have the requester info, fetch it from user_profiles
        if (!requesterName || requesterName === data.userId) {
          try {
            const profileQ = query(
              collection(db, COLLECTIONS.USER_PROFILES),
              where('uid', '==', data.userId)
            );
            const profileSnap = await getDocs(profileQ);
            if (!profileSnap.empty) {
              const profile = profileSnap.docs[0].data();
              requesterName = profile.displayName || profile.email || data.userId;
              requesterEmail = profile.email || data.requesterEmail || '';
            } else {
              requesterName = data.requesterName || data.userId;
              requesterEmail = data.requesterEmail || '';
            }
          } catch (error) {
            console.error('Error fetching user profile for requester:', error);
            requesterName = data.requesterName || data.userId;
            requesterEmail = data.requesterEmail || '';
          }
        }
        
        return {
          id: doc.id,
          requesterId: data.userId,
          requesterEmail,
          requesterName,
          approverEmail: data.approverEmail,
          status: data.status,
          requestedAt: data.requestedAt?.toDate() || new Date(),
          respondedAt: data.respondedAt?.toDate() || undefined,
          responseMessage: data.responseMessage || ''
        };
      }));
      
      return requests as ApproverRequest[];
    } catch (error) {
      console.error('Error getting pending approver requests:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Respond to approver request
  async respondToApproverRequest(requestId: string, status: 'accepted' | 'rejected', message?: string): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Please sign in to continue.');
      }

      // Update the existing credit_approvers document
      const requestRef = doc(db, COLLECTIONS.CREDIT_APPROVERS, requestId);
      const requestDoc = await getDoc(requestRef);
      
      if (!requestDoc.exists()) {
        throw new Error('Approver request not found.');
      }

      const requestData = requestDoc.data();
      const requesterId = requestData.userId;

      // Update the document with response
      await updateDoc(requestRef, {
        status,
        respondedAt: serverTimestamp(),
        responseMessage: message || '',
        approverUid: status === 'accepted' ? user.uid : null,
        approverName: status === 'accepted' ? (user.displayName || user.email!) : null
      });

      // If accepted, update the requester's user profile
      if (status === 'accepted') {
        // Get all accepted approvers for the requester
        const approversQ = query(
          collection(db, COLLECTIONS.CREDIT_APPROVERS),
          where('userId', '==', requesterId),
          where('status', '==', 'accepted')
        );
        const approversSnap = await getDocs(approversQ);
        const activeApprovers = approversSnap.docs.length;
        const totalApproversQ = query(
          collection(db, COLLECTIONS.CREDIT_APPROVERS),
          where('userId', '==', requesterId)
        );
        const totalApproversSnap = await getDocs(totalApproversQ);
        const totalApprovers = totalApproversSnap.docs.length;
        // Update the requester's user profile
        await updateDoc(doc(db, COLLECTIONS.USER_PROFILES, requesterId), {
          canCreateCreditedTasks: activeApprovers > 0,
          hasApprovers: totalApprovers > 0,
          activeApprovers,
          totalApprovers,
          updatedAt: serverTimestamp()
        });
      }

      console.log('Credit approver request responded to:', requestId);

    } catch (error) {
      console.error('Error responding to approver request:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Check if user can create credited tasks
  async canCreateCreditedTasks(): Promise<boolean> {
    try {
      const profile = await this.getUserProfile();
      return profile.canCreateCreditedTasks;
    } catch (error) {
      console.error('Error checking credited tasks permission:', error);
      return false;
    }
  },

  // Get users for whom the current user is an approver
  async getUsersIApprove(): Promise<{ userId: string; displayName: string; email: string; photoURL?: string }[]> {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Please sign in to continue.');
      const q = query(
        collection(db, COLLECTIONS.CREDIT_APPROVERS),
        where('approverUid', '==', user.uid),
        where('status', '==', 'accepted')
      );
      const querySnapshot = await getDocs(q);
      const users = await Promise.all(querySnapshot.docs.map(async docSnap => {
        const data = docSnap.data();
        let displayName = data.requesterName;
        let email = data.requesterEmail;
        let photoURL = undefined;
        // Always fetch from user_profiles for accuracy
        const profileQ = query(
          collection(db, COLLECTIONS.USER_PROFILES),
          where('uid', '==', data.userId)
        );
        const profileSnap = await getDocs(profileQ);
        if (!profileSnap.empty) {
          const profile = profileSnap.docs[0].data();
          displayName = profile.displayName || data.userId;
          email = profile.email || data.requesterEmail || '';
          photoURL = profile.photoURL;
        } else {
          displayName = data.requesterName || data.userId;
          email = data.requesterEmail || '';
        }
        return {
          userId: data.userId,
          displayName,
          email,
          photoURL
        };
      }));
      return users;
    } catch (error) {
      console.error('Error getting users I approve:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Get all credit approvers for current user (including pending and rejected)
  async getAllCreditApprovers(): Promise<CreditApprover[]> {
    try {
      const userId = getCurrentUserId();
      
      const q = query(
        collection(db, COLLECTIONS.CREDIT_APPROVERS),
        where('userId', '==', userId),
        orderBy('requestedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          requestedAt: data.requestedAt?.toDate() || new Date(),
          respondedAt: data.respondedAt?.toDate() || undefined
        };
      }) as CreditApprover[];
    } catch (error) {
      console.error('Error getting all credit approvers:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Check if user has at least one accepted credit approver (real-time)
  async hasAcceptedCreditApprover(): Promise<boolean> {
    try {
      const userId = getCurrentUserId();
      const q = query(
        collection(db, COLLECTIONS.CREDIT_APPROVERS),
        where('userId', '==', userId),
        where('status', '==', 'accepted')
      );
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      console.error('Error checking for accepted credit approver:', error);
      return false;
    }
  },


}; 